/**
 * mt-partner-company.js
 * Partner Company - Step-based card stack effect
 *
 * 坐标体系：
 *   初始态: 第0张在0px，后续每张向右排开 (cardWidth+gap)
 *   堆叠态: 已堆叠的卡在左侧 i*stackOffset，未堆叠的紧跟堆叠区右边
 *   尾部卡: 始终在展开队列末尾，全堆完后右对齐 track
 * JS初始化完成前，CSS visibility:hidden 隐藏所有卡片，避免 defer 加载期间的闪烁。
 */

(function () {
  'use strict';

  /* ============================================================
     CardSlider - 单张卡片内部轮播
     ============================================================ */
  class CardSlider {
    constructor(cardEl) {
      this.card = cardEl;
      this.slider = cardEl.querySelector('.mt-pc-slider');
      this.track = cardEl.querySelector('.mt-pc-slides-track');
      this.slides = Array.from(cardEl.querySelectorAll('.mt-pc-slide'));
      this.innerDots = Array.from(cardEl.querySelectorAll('.mt-pc-inner-dot'));
      this.prevBtn = cardEl.querySelector('.mt-pc-prev');
      this.nextBtn = cardEl.querySelector('.mt-pc-next');

      this.total = this.slides.length;
      this.current = 0;

      if (this.total <= 1) return;
      this._bindEvents();
    }

    _bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.go(this.current - 1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.go(this.current + 1);
        });
      }
      let startX = 0;
      this.slider.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
      }, { passive: true });
      this.slider.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) this.go(this.current + (diff > 0 ? 1 : -1));
      }, { passive: true });
    }

    go(index) {
      this.current = ((index % this.total) + this.total) % this.total;
      this.track.style.transform = `translateX(-${this.current * 100}%)`;
      this.innerDots.forEach((d, i) => d.classList.toggle('active', i === this.current));
    }
  }

  /* ============================================================
     PartnerCompany - 阶跃式堆叠主逻辑
     ============================================================ */
  class PartnerCompany {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.track = sectionEl.querySelector('.mt-partner-track');
      this.cards = Array.from(sectionEl.querySelectorAll('.mt-partner-card'));
      this.dots = Array.from(sectionEl.querySelectorAll('.mt-partner-dot'));

      if (!this.track || this.cards.length === 0) return;

      this.imageCards = this.cards.filter((c) => c.classList.contains('is-image-card'));
      this.tailCard = this.cards.find((c) => c.classList.contains('is-tail-card'));

      this.cardCount = this.imageCards.length;
      this.gap = 20;
      this.stackOffset = 30;
      this.stepViewportFactor = 0.35 / 1.5;
      this.stepHeight = window.innerHeight * this.stepViewportFactor;
      this._stackedCount = -1;
      this._isMobile = window.innerWidth <= 767;
      this._transitionEnabled = sectionEl.dataset.mtTransitionEnabled === 'true';
      this._enterPlayed =
        sectionEl.dataset.mtPartnerEnterPlayed === 'true' ||
        sectionEl.classList.contains('mt-partner-entered');
      this._onTransitionReady = null;
      this._onTransitionCommit = null;
      this._onTransitionReset = null;

      this.cardSliders = this.imageCards.map((card) => new CardSlider(card));
      this._init();
    }

    _setEnteredState() {
      this._enterPlayed = true;
      this.section.dataset.mtPartnerEnterPlayed = 'true';
      this.section.classList.remove('mt-partner-enter');
      this.section.classList.add('mt-partner-entered');
      this.section.style.opacity = '1';
      this.section.style.transform = '';
    }

    _isTransitionControlled() {
      return (
        this._transitionEnabled &&
        window.innerWidth >= 768 &&
        window.MTPageTransition &&
        window.MTPageTransition.isEnabled()
      );
    }

    _isTransitionReady() {
      if (!this._isTransitionControlled()) return true;
      return window.MTPageTransition.isReady(this.section);
    }

    _getTransitionState() {
      if (!this._isTransitionControlled()) return null;
      return window.MTPageTransition.getState(this.section);
    }

    _getScrolledDistance() {
      const transitionState = this._getTransitionState();

      if (transitionState && transitionState.active && !transitionState.ready) {
        return 0;
      }

      if (transitionState && transitionState.committed && typeof transitionState.scrollOriginY === 'number') {
        return Math.max(0, window.scrollY - transitionState.scrollOriginY);
      }

      return Math.max(0, window.scrollY - this._sectionTop());
    }

    _syncForCurrentProgress(animate) {
      if (!this._isTransitionReady()) {
        this._setPositions(this._buildPositions(0), false);
        return;
      }

      const scrolled = this._getScrolledDistance();
      const count = this._scrollToCount(scrolled);
      this._setPositions(this._buildPositions(count), animate);
    }

    _bindTransitionLifecycle() {
      if (!this._isTransitionControlled()) return;

      this._onTransitionCommit = () => {
        const transitionState = this._getTransitionState();
        if (transitionState && transitionState.committed) {
          this._setEnteredState();
        }
        this._syncForCurrentProgress(false);
      };

      this._onTransitionReset = () => {
        this._stackedCount = -1;
        this._setPositions(this._buildPositions(0), false);
      };

      this.section.addEventListener('mt:page-transition-commit', this._onTransitionCommit);
      this.section.addEventListener('mt:page-transition-reset', this._onTransitionReset);
    }

    _init() {
      this._calcLayout();
      this._setScrollSpace();
      this._bindTransitionLifecycle();

      // 1. 先静默（无动画）设好当前滚动位置对应的状态
      const initScrolled = this._getScrolledDistance();
      const initCount = this._scrollToCount(initScrolled);
      this._setPositions(this._buildPositions(initCount), false);

      // 2. 添加 is-ready，CSS visibility 打开，卡片从正确位置出现（不会有飞入动画）
      this.section.classList.add('is-ready');

      // 3. 入场动画：组件从底部滑入 + 淡入
      this._setupEnterAnimation();

      // 4. 注册滚动/resize 事件
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
    }

    _setupEnterAnimation() {
      const section = this.section;

      if (this._enterPlayed) {
        this._setEnteredState();
        return;
      }

      if (this._isTransitionControlled() && !this._isTransitionReady()) {
        if (!this._onTransitionReady) {
          this._onTransitionReady = () => {
            this._playEnterAnimation();
            this._onTransitionReady = null;
          };
          section.addEventListener('mt:page-transition-ready', this._onTransitionReady, { once: true });
        }
        return;
      }

      if (this._enterPlayed) return;

      // 如果组件初始化时已经在视口内（页面一开始就显示该组件），直接播放入场动画
      const rect = section.getBoundingClientRect();
      const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (alreadyVisible) {
        // 已在视口内：稍作延迟后播放，确保样式已生效
        requestAnimationFrame(() => {
          this._playEnterAnimation();
        });
        return;
      }

      // 不在视口内：用 IntersectionObserver 等待进入视口再触发
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._playEnterAnimation();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.08 }
      );
      observer.observe(section);
    }

    _playEnterAnimation() {
      if (this._enterPlayed) {
        this._setEnteredState();
        return;
      }

      this.section.classList.add('mt-partner-enter');
      this.section.addEventListener('animationend', () => {
        this._setEnteredState();
      }, { once: true });
    }

    _sectionTop() {
      return this.section.getBoundingClientRect().top + window.scrollY;
    }

    _scrollToCount(scrolled) {
      return Math.min(
        Math.floor(scrolled / this.stepHeight),
        this.cardCount
      );
    }

    _calcLayout() {
      this._isMobile = window.innerWidth <= 767;

      this.cardWidth = this.imageCards[0]
        ? parseFloat(getComputedStyle(this.imageCards[0]).width)
        : 360;
      this.trackWidth = parseFloat(getComputedStyle(this.track).width);
      this.cardHeight = this.imageCards[0]
        ? parseFloat(getComputedStyle(this.imageCards[0]).height)
        : 480;

      if (this._isMobile) {
        // 移动端：纵向堆叠，每张卡向上偏移 stackOffset 露出上一张
        this.mobileStackOffsetY = 20; // 每张叠起后向上偏 20px 露出
      } else {
        // 尾部卡右对齐 track 时的 X
        this.tailFinalX = this.trackWidth - this.cardWidth;
        // 最后一张图片卡此时停在尾部卡左边
        this.lastCardFinalX = this.tailFinalX - this.cardWidth - this.gap;
      }

      this.stepHeight = window.innerHeight * this.stepViewportFactor;
    }

    _setScrollSpace() {
      const vh = window.innerHeight;
      if (this._isMobile) {
        // 移动端：每张图片卡各占一个步长，尾部卡不需要额外步长
        this.section.style.height = vh + this.stepHeight * this.cardCount + 'px';
      } else {
        // 只需给图片卡的堆叠留滚动空间，尾部卡固定不动，不需要额外步长
        this.section.style.height = vh + this.stepHeight * this.cardCount + 'px';
      }
    }

    /**
     * 根据堆叠数构建每张卡片的目标坐标
     * 移动端：translateY（纵向堆叠，新卡从右下滑入）
     * PC端：translateX（横向堆叠）
     */
    _buildPositions(stackedCount) {
      if (this._isMobile) {
        return this._buildPositionsMobile(stackedCount);
      }
      return this._buildPositionsPC(stackedCount);
    }

    /**
     * 移动端：纵向堆叠
     *  - 已堆叠的卡：从下往上，i 越大 Y 越大（上层卡在前）
     *    第0张在最底层，Y=0
     *    第i张在 i * mobileStackOffsetY（越叠越往上偏一点，营造扇形感）
     *  - 当前"顶层"展示的卡（index = stackedCount - 1 如果>0 else 0）在 Y=0
     *  - 未堆叠的下一张：在屏幕外右下方待命
     *  - 尾部卡：全部堆完后出现在最顶层
     */
    _buildPositionsMobile(stackedCount) {
      // stackedCount 表示"已被翻过的卡数"
      // 当前顶层展示的卡 index = stackedCount（还未被翻过的第一张）
      const currentIdx = stackedCount;
      // 判断滚动方向（用于区分"入场"还是"退场"）
      const goingForward = stackedCount >= this._stackedCount;

      const imageStates = this.imageCards.map((_, i) => {
        if (i < stackedCount) {
          // 已经"翻过"的卡：直接叠在原位，不做任何偏移
          return {
            x: 0,
            y: 0,
            scale: 1,
            zIndex: i + 1,
            visible: true,
          };
        } else if (i === currentIdx) {
          // 当前顶层展示的卡
          return {
            x: 0,
            y: 0,
            scale: 1,
            zIndex: this.cardCount + 2,
            visible: true,
          };
        } else {
          // 未到的卡：
          // 向前滚时：退到右下待命（透明）
          // 向后滚时：i === stackedCount+1 是"刚被退回的下一张"，也保持右下透明
          return {
            x: this.trackWidth * 0.35,
            y: this.cardHeight * 0.35,
            scale: 0.9,
            zIndex: 0,
            visible: false,
          };
        }
      });

      // 尾部卡：全部图片卡翻完后出现
      const tailState = stackedCount === this.cardCount
        ? { x: 0, y: 0, scale: 1, zIndex: this.cardCount + 1, visible: true }
        : { x: this.trackWidth * 0.35, y: this.cardHeight * 0.35, scale: 0.9, zIndex: 0, visible: false };

      return { imageStates, tailState, stackedCount, isMobile: true, goingForward };
    }

    /**
     * PC端：横向堆叠（原逻辑）
     */
    _buildPositionsPC(stackedCount) {
      const lastIdx = this.cardCount - 1;

      // 堆叠区右边缘 + gap（未堆叠卡从这里开始排）
      // stackedCount=0 时无堆叠区，从 0px 开始
      const stackRightEdge = stackedCount > 0
        ? (stackedCount - 1) * this.stackOffset + this.cardWidth + this.gap
        : 0;

      const imageXs = this.imageCards.map((_, i) => {
        if (i < stackedCount) {
          // 堆叠态：
          // 最后一张图片卡特殊——停在尾部卡左边（尾部卡此时右对齐 track）
          // 其余卡左侧叠扇依次 stackOffset 错位
          return i === lastIdx ? this.lastCardFinalX : i * this.stackOffset;
        } else {
          // 展开态：紧跟堆叠区右边依次排列
          const pos = i - stackedCount; // 0, 1, 2...
          return stackRightEdge + pos * (this.cardWidth + this.gap);
        }
      });

      // 尾部卡：跟在最后一张图片卡右边；全部堆完时右对齐 track
      const lastX = imageXs[lastIdx];
      const tailX = stackedCount === this.cardCount
        ? this.tailFinalX
        : lastX + this.cardWidth + this.gap;

      return { imageXs, tailX, stackedCount };
    }

    _setArrowsHidden(card, hidden) {
      const arrows = card.querySelector('.mt-partner-card-arrows');
      if (!arrows) return;
      arrows.classList.toggle('is-hidden', hidden);
    }

    _updateArrowVisibilityPC(imageXs) {
      this.imageCards.forEach((card, i) => {
        const currentX = imageXs[i];
        const covered = imageXs.some((nextX, nextIndex) => {
          if (nextIndex <= i) return false;
          return nextX > currentX && nextX < currentX + this.cardWidth;
        });

        this._setArrowsHidden(card, covered);
      });
    }

    _resetArrowVisibility() {
      this.imageCards.forEach((card) => this._setArrowsHidden(card, false));
    }

    /**
     * 将 _buildPositions 的结果应用到 DOM
     * animate=false：不加 transition（初始化/resize 用）
     */
    _setPositions(positions, animate) {
      if (animate && positions.stackedCount === this._stackedCount) return;
      this._stackedCount = positions.stackedCount;

      if (positions.isMobile) {
        this._setPositionsMobile(positions, animate);
      } else {
        this._setPositionsPC(positions, animate);
      }
    }

    _setPositionsMobile({ imageStates, tailState, stackedCount, goingForward }, animate) {
      this._resetArrowVisibility();

      const tr = animate
        ? 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease'
        : 'none';
      // 退出时：位移 + 同步淡出，动画结束即消失
      const trExit = animate
        ? 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.55s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'none';

      // 只有 stackedCount > 0 时才有退出卡（第一张不退）
      let exitCardIdx = -1;
      if (animate && !goingForward && stackedCount > 0 && stackedCount < this.cardCount) {
        exitCardIdx = stackedCount;
      }

      this.imageCards.forEach((card, i) => {
        const s = imageStates[i];
        if (i === exitCardIdx) {
          // 旧顶层卡：位移到右下同时淡出，同步消失
          card.style.transition = trExit;
          card.style.transform = `translate(${this.trackWidth * 0.35}px, ${this.cardHeight * 0.35}px) scale(0.9)`;
          card.style.opacity = '0';
          card.style.zIndex = s.zIndex;
          card.style.pointerEvents = 'none';
        } else {
          card.style.transition = tr;
          card.style.transform = `translate(${s.x}px, ${s.y}px) scale(${s.scale})`;
          card.style.opacity = s.visible ? '1' : '0';
          card.style.zIndex = s.zIndex;
          card.style.pointerEvents = s.visible ? '' : 'none';
        }
        if (this.dots[i]) {
          this.dots[i].classList.toggle('active', i < stackedCount);
        }
      });

      if (this.tailCard) {
        this.tailCard.style.transition = tr;
        this.tailCard.style.transform = `translate(${tailState.x}px, ${tailState.y}px) scale(${tailState.scale || 1})`;
        this.tailCard.style.opacity = tailState.visible ? '1' : '0';
        this.tailCard.style.zIndex = tailState.zIndex || 0;
        this.tailCard.style.left = '0';
        this.tailCard.style.right = 'auto';
      }
    }

    _setPositionsPC({ imageXs, tailX, stackedCount }, animate) {
      const tr = animate
        ? 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'none';

      this.imageCards.forEach((card, i) => {
        card.style.transition = tr;
        card.style.transform = `translateX(${imageXs[i]}px)`;
        card.style.zIndex = i + 1;
        if (this.dots[i]) {
          this.dots[i].classList.toggle('active', i < stackedCount);
        }
      });

      this._updateArrowVisibilityPC(imageXs);

      if (this.tailCard) {
        this.tailCard.style.transition = tr;
        this.tailCard.style.transform = `translateX(${tailX}px)`;
        this.tailCard.style.left = '0';
        this.tailCard.style.right = 'auto';
        this.tailCard.style.zIndex = this.cardCount + 1;
      }
    }

    _onScroll() {
      this._syncForCurrentProgress(true);
    }

    _onResize() {
      const wasMobile = this._isMobile;
      this._calcLayout();
      this._setScrollSpace();
      this._stackedCount = -1;

      // 移动端切换时，重置卡片 opacity
      if (this._isMobile !== wasMobile) {
        this.imageCards.forEach((card) => {
          card.style.opacity = '';
          card.style.transform = '';
        });
        if (this.tailCard) {
          this.tailCard.style.opacity = '';
          this.tailCard.style.transform = '';
        }
      }

      this._syncForCurrentProgress(false);
    }

    destroy() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._onTransitionReady) {
        this.section.removeEventListener('mt:page-transition-ready', this._onTransitionReady);
        this._onTransitionReady = null;
      }
      if (this._onTransitionCommit) {
        this.section.removeEventListener('mt:page-transition-commit', this._onTransitionCommit);
        this._onTransitionCommit = null;
      }
      if (this._onTransitionReset) {
        this.section.removeEventListener('mt:page-transition-reset', this._onTransitionReset);
        this._onTransitionReset = null;
      }
    }
  }

  /* -- 初始化所有实例 -- */
  function initAll() {
    document.querySelectorAll('.mt-partner-section').forEach((el) => {
      if (el._partnerCompanyInstance) {
        el._partnerCompanyInstance.destroy();
      }
      el._partnerCompanyInstance = new PartnerCompany(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.mt-partner-section')) initAll();
  });
})();
